<template>
    <div
        class="omocom-insurance-coverage-product"
        :class="{
            'omocom-insurance-coverage-product--selected': !isInfoMode,
        }"
    >
        <p v-if="isInfoMode" class="omocom-insurance-coverage-product__title">
            {{ $t(`pages.booking_request.insurance_step.${selectedCoverageKey}`) }}
        </p>
        <template v-else>
            <p v-if="isIncluded" class="omocom-insurance-coverage-product__included">
                {{ $t('pages.booking_request.insurance_step.included') }}
            </p>
            <div v-else class="omocom-insurance-coverage-product__price">
                <strong>
                    {{ formattedPricePerNight }}
                </strong>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <span>&nbsp;/&nbsp;</span>
                <span class="omocom-insurance-coverage-product__price-type">{{ $t('common.night') }}</span>
            </div>
        </template>

        <p class="omocom-insurance-coverage-product__deductible">
            {{ $t('pages.booking_request.insurance_step.deductible', { price: formattedDeductiblePrice }) }}
        </p>

        <Accordion :items="filteredAccordionItems" :allow-multiple-open="false" mode="minimalistic">
            <template #item-0>
                <ul>
                    <li>{{ $t('pages.booking_request.insurance_step.full_comprehensive_insurance.info1') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.full_comprehensive_insurance.info2') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.full_comprehensive_insurance.info3') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.full_comprehensive_insurance.info4') }}</li>
                </ul>
            </template>
            <template #item-1>
                <ul>
                    <li>
                        <strong>{{
                            $t('pages.booking_request.insurance_step.roadside_assistance.info1', {
                                price: formattedRoadsideAssistancePrice,
                            })
                        }}</strong>
                    </li>
                    <li>{{ $t('pages.booking_request.insurance_step.roadside_assistance.info2') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.roadside_assistance.info3') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.roadside_assistance.info4') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.roadside_assistance.info5') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.roadside_assistance.info6') }}</li>
                </ul>
            </template>
            <template #item-2>
                <ul>
                    <li>{{ $t('pages.booking_request.insurance_step.misfortune_coverage.info1') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.misfortune_coverage.info2') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.misfortune_coverage.info3') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.misfortune_coverage.info4') }}</li>
                </ul>
            </template>
            <template #item-3>
                {{ $t('pages.booking_request.insurance_step.emergency_transportation.info') }}
            </template>
            <template #item-4>
                {{ $t('pages.booking_request.insurance_step.cancellation_insurance.info') }}
            </template>
            <template #item-5>
                {{ $t('pages.booking_request.insurance_step.reimbursement_of_repair_delay.info') }}
            </template>
        </Accordion>
    </div>
</template>

<script>
import Accordion from '~/components/Accordion';
import { OMOCOM_COMPLETE, OMOCOM_PLUS, OMOCOM_BASIC } from '~/config/productTypes';

export default {
    name: 'OmocomInsuranceCoverageProduct',
    components: { Accordion },
    props: {
        isInteriorInsuranceIncluded: {
            type: Boolean,
            required: true,
        },
        selectedCoverageProduct: {
            type: Object,
            required: true,
        },
        isDowngradeDisabled: {
            type: Boolean,
            default: false,
        },
        insuranceCoverageProducts: {
            type: Array,
            default: () => [],
        },
        mode: {
            type: String,
            default: '',
        },
        currentCoverageKey: {
            type: String,
            default: OMOCOM_BASIC,
        },
        hideErrorItems: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        selectedCoverageKey() {
            return this.selectedCoverageProduct.key;
        },
        baseProduct() {
            if (this.isDowngradeDisabled && this.currentCoverageKey !== OMOCOM_BASIC) {
                return this.insuranceCoverageProducts.find(product => product.key === this.currentCoverageKey);
            }

            return this.insuranceCoverageProducts.find(product => product.key === OMOCOM_BASIC);
        },
        pricePerNight() {
            const difference = this.selectedCoverageProduct.price - this.baseProduct?.price;

            return difference || 0;
        },
        isIncluded() {
            // In case downgrade is disabled we want to show the current coverage as included
            if (this.isDowngradeDisabled && this.selectedCoverageProduct.key === this.currentCoverageKey) {
                return true;
            }

            return this.selectedCoverageProduct.key === OMOCOM_BASIC && this.currentCoverageKey === OMOCOM_BASIC;
        },
        formattedPricePerNight() {
            if (this.isIncluded) {
                return 0;
            }

            return `+ ${this.formattedPrice(this.pricePerNight, this.selectedCoverageProduct.currency, 2)}`;
        },
        formattedDeductiblePrice() {
            const deductibleAmount = this.selectedCoverageProduct.specifications?.deductibleAmount;

            return this.formattedPrice(deductibleAmount, this.selectedCoverageProduct.currency, 0);
        },
        formattedRoadsideAssistancePrice() {
            const deductibleAmount = this.selectedCoverageProduct.specifications?.roadsideAssistanceAmount;

            return this.formattedPrice(deductibleAmount, this.selectedCoverageProduct.currency, 0);
        },
        isInfoMode() {
            return this.mode === 'info';
        },
        accordionItems() {
            const items = [
                {
                    title: this.$t('pages.booking_request.insurance_step.full_comprehensive_insurance.title'),
                    status: 'success',
                },
                { title: this.$t('pages.booking_request.insurance_step.roadside_assistance.title'), status: 'success' },
                {
                    title: this.$t('pages.booking_request.insurance_step.emergency_transportation.title'),
                    status: [OMOCOM_PLUS, OMOCOM_COMPLETE].includes(this.selectedCoverageKey) ? 'success' : 'error',
                },
                {
                    title: this.$t('pages.booking_request.insurance_step.cancellation_insurance.title'),
                    status: this.selectedCoverageKey === OMOCOM_COMPLETE ? 'success' : 'error',
                },
                {
                    title: this.$t('pages.booking_request.insurance_step.reimbursement_of_repair_delay.title'),
                    status: this.selectedCoverageKey === OMOCOM_COMPLETE ? 'success' : 'error',
                },
            ];

            if (this.isInteriorInsuranceIncluded) {
                items.splice(2, 0, {
                    title: this.$t('pages.booking_request.insurance_step.misfortune_coverage.title'),
                    status: 'success',
                });
            }

            return items;
        },
        filteredAccordionItems() {
            if (this.hideErrorItems) {
                return this.accordionItems.filter(item => item.status !== 'error');
            }

            return this.accordionItems;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.omocom-insurance-coverage-product {
    @extend %card;
    padding: 24px 16px;

    &--selected {
        background: $bright-blue-light;
    }

    &__title {
        @include font-size(20px);
        font-weight: bold;
        margin-bottom: 24px;
    }

    &__included {
        font-weight: bold;
        margin-bottom: 16px;
    }

    &__price {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
    }

    &__price-type {
        @include font-size(14px);
    }

    &__deductible {
        @include font-size(18px);
    }

    ul {
        list-style: disc;
        margin-left: 16px;

        li:not(:last-child) {
            margin-bottom: 8px;
        }
    }
}
</style>
